import React from 'react';
import { ButtonProps, InputProps } from '@apia/theme';
import { Checkbox as AppCheckbox } from '@apia/components';
import { useField } from '../hooks/useField';
import { TDefaultFieldType } from './types';
import { useValidationClass } from '../hooks/useValidationClass';
import { FieldWrapper } from './FieldWrapper';

export type TCheckboxProps = Omit<
  ButtonProps,
  'onChange' | 'defaultChecked' | 'defaultValue'
> &
  Pick<InputProps, 'onChange'>;

export type TCheckbox = TCheckboxProps &
  TDefaultFieldType<boolean | string> & { native?: boolean };

export const Checkbox = React.forwardRef<HTMLButtonElement, TCheckbox>(
  (
    {
      avoidSemicolon,
      className,
      label,
      native,
      onChange: outerOnChange,
      type,
      /**
       * useField
       */
      avoidRegisterIfExists,
      formName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });
    const {
      value: innerValue,
      onChange,
      updateValidations,
      isTouched,
      ...inputProps
    } = useField<boolean | string>({
      avoidRegisterIfExists,
      formName,
      name,
      initialValue:
        ((initialValue as unknown) === true || initialValue === 'on') ?? false,
      removeOnUnmount,
      submitValueParser,
      validationFunction: actualValidationFunction,
      validationRules: actualValidationRules,
    });

    return (
      <FieldWrapper
        name={name}
        label={label}
        className={`checkbox ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <AppCheckbox
          {...props}
          {...inputProps}
          native={native}
          aria-label={props['aria-label'] ?? label}
          onChange={(ev) => {
            onChange(ev.target.checked);
            if (outerOnChange) outerOnChange(ev);
          }}
          checked={innerValue === true || innerValue === 'on'}
          ref={ref}
        />
      </FieldWrapper>
    );
  },
);

Checkbox.displayName = 'Checkbox';
